import { Link } from 'react-router-dom'
import { LayoutDashboard, LayoutGrid, Map as MapIcon, Megaphone, MonitorPlay, UserCog, UsersRound } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { isDirectorEnabled } from '@/lib/features'
import { cn } from '@/lib/utils'
import { useAuth } from '@/state/AuthContext'
import '@/components/workspace/view-switcher.css'

export type EventView = 'cameras' | 'director' | 'multiview' | 'map' | 'athletes' | 'team' | 'sponsors'

interface Props {
  eventId: string
  /** Active view — omit for adjacent surfaces (e.g. Settings) where no live view should appear selected. */
  active?: EventView
  className?: string
}

interface ViewDef {
  key: EventView
  label: string
  icon: LucideIcon
  to: (eventId: string) => string
}

/** The workspace's own names, so a view reads the same from every page: Overview, Multiview, Map, Athletes and Team,
 *  then the rest. */
const VIEWS: ViewDef[] = [
  { key: 'cameras', label: 'Overview', icon: LayoutDashboard, to: (id) => `/events/${id}` },
  { key: 'multiview', label: 'Multiview', icon: LayoutGrid, to: (id) => `/events/${id}/multiview` },
  { key: 'map', label: 'Map', icon: MapIcon, to: (id) => `/events/${id}/map` },
  { key: 'athletes', label: 'Athletes', icon: UsersRound, to: (id) => `/events/${id}/athletes` },
  { key: 'team', label: 'Team', icon: UserCog, to: (id) => `/events/${id}/team` },
  { key: 'sponsors', label: 'Sponsors', icon: Megaphone, to: (id) => `/events/${id}/sponsors` },
  { key: 'director', label: 'AI Producer', icon: MonitorPlay, to: (id) => `/events/${id}/canvas` },
]

/** The event's views as links, in the workspace's view switcher (Play Tabs look). Labels stay visible; on a narrow
 *  screen the row scrolls. */
export function EventViewSwitcher({ eventId, active, className }: Props) {
  const { organization } = useAuth()
  const visibleViews = isDirectorEnabled(organization)
    ? VIEWS
    : VIEWS.filter((view) => view.key !== 'director')

  return (
    <nav aria-label="Event views" className={cn('pw-view-switcher', className)}>
      {visibleViews.map((view) => {
        const Icon = view.icon
        return (
          <Link
            key={view.key}
            to={view.to(eventId)}
            aria-current={view.key === active ? 'page' : undefined}
          >
            <Icon aria-hidden="true" />
            <span>{view.label}</span>
          </Link>
        )
      })}
    </nav>
  )
}
